/**
 * Admin Job Posting Settings
 * Control job posting configuration per country
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Settings,
  Globe,
  DollarSign,
  Check,
  X,
  Loader2,
  Save,
  RefreshCw,
  Briefcase,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminJobPostingSettings = () => {
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [activeCountry, setActiveCountry] = useState('SL');
  const [pendingJobs, setPendingJobs] = useState([]);

  useEffect(() => {
    fetchConfigs();
    fetchPendingJobs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/job-posting/admin/config`);
      
      if (response.data.success) {
        setConfigs(response.data.configs || []);
        setAllCategories(response.data.available_categories || []);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load configurations',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/job-posting/admin/pending-jobs`);
      if (response.data.success) {
        setPendingJobs(response.data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching pending jobs:', error);
    }
  };

  const getCountryConfig = (country) => {
    return configs.find(c => c.country === country) || {
      country,
      enabled: true,
      posting_fee: 0,
      featured_fee: 0,
      urgent_fee: 0,
      require_approval: false,
      max_active_jobs_free: 5,
      categories_enabled: allCategories.map(c => c.slug),
      locations: [],
    };
  };

  const handleSaveConfig = async (country) => {
    const config = getCountryConfig(country);
    
    try {
      setSaving(true);
      const response = await axios.put(`${API_URL}/api/job-posting/admin/config/${country}`, {
        enabled: config.enabled,
        posting_fee: config.posting_fee || 0,
        featured_fee: config.featured_fee || 0,
        urgent_fee: config.urgent_fee || 0,
        require_approval: config.require_approval || false,
        max_active_jobs_free: config.max_active_jobs_free || 5,
        categories_enabled: config.categories_enabled || [],
        locations: config.locations || [],
      });
      
      if (response.data.success) {
        toast({
          title: 'Settings saved',
          description: `Configuration updated for ${country}`,
        });
        fetchConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCategory = async (country, category, enabled) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/job-posting/admin/config/${country}/toggle-category?category=${category}&enabled=${enabled}`
      );
      
      if (response.data.success) {
        toast({
          title: enabled ? 'Category enabled' : 'Category disabled',
          description: response.data.message,
        });
        fetchConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update category',
        variant: 'destructive',
      });
    }
  };

  const handleApproveJob = async (jobId, approved) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/job-posting/admin/job/${jobId}/approve?approved=${approved}`
      );
      
      if (response.data.success) {
        toast({
          title: approved ? 'Job approved' : 'Job rejected',
          description: response.data.message,
        });
        fetchPendingJobs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update job',
        variant: 'destructive',
      });
    }
  };

  const updateConfigField = (country, field, value) => {
    setConfigs(prev => {
      const existing = prev.find(c => c.country === country);
      if (existing) {
        return prev.map(c => c.country === country ? { ...c, [field]: value } : c);
      }
      return [...prev, { country, [field]: value }];
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentConfig = getCountryConfig(activeCountry);

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-job-posting-settings">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Job Posting Settings
          </h1>
          <p className="text-gray-600">Configure job posting rules and fees by country</p>
        </div>

        <Tabs value={activeCountry} onValueChange={setActiveCountry}>
          <TabsList className="mb-6">
            {['SL', 'UK'].map(country => (
              <TabsTrigger key={country} value={country} className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {country === 'SL' ? 'Sri Lanka' : 'United Kingdom'}
              </TabsTrigger>
            ))}
          </TabsList>

          {['SL', 'UK'].map(country => (
            <TabsContent key={country} value={country} className="space-y-6">
              {/* General Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                  <CardDescription>Enable/disable job posting and set fees</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Enable Job Posting</Label>
                      <p className="text-sm text-gray-500">Allow employers to post jobs in this country</p>
                    </div>
                    <Switch
                      checked={currentConfig.enabled}
                      onCheckedChange={(checked) => updateConfigField(country, 'enabled', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Require Admin Approval</Label>
                      <p className="text-sm text-gray-500">Jobs must be approved before going live</p>
                    </div>
                    <Switch
                      checked={currentConfig.require_approval}
                      onCheckedChange={(checked) => updateConfigField(country, 'require_approval', checked)}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Basic Posting Fee</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-500">{country === 'SL' ? 'Rs' : '£'}</span>
                        <Input
                          type="number"
                          value={currentConfig.posting_fee || 0}
                          onChange={(e) => updateConfigField(country, 'posting_fee', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Featured Fee</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-500">{country === 'SL' ? 'Rs' : '£'}</span>
                        <Input
                          type="number"
                          value={currentConfig.featured_fee || 0}
                          onChange={(e) => updateConfigField(country, 'featured_fee', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Urgent Fee</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-500">{country === 'SL' ? 'Rs' : '£'}</span>
                        <Input
                          type="number"
                          value={currentConfig.urgent_fee || 0}
                          onChange={(e) => updateConfigField(country, 'urgent_fee', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Max Free Active Jobs per Employer</Label>
                    <Input
                      type="number"
                      className="w-32 mt-1"
                      value={currentConfig.max_active_jobs_free || 5}
                      onChange={(e) => updateConfigField(country, 'max_active_jobs_free', parseInt(e.target.value) || 5)}
                    />
                  </div>

                  <Button onClick={() => handleSaveConfig(country)} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Category Controls */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    Job Categories
                  </CardTitle>
                  <CardDescription>Enable or disable specific job categories for this country</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {allCategories.map(category => {
                      const isEnabled = (currentConfig.categories_enabled || []).includes(category.slug);
                      return (
                        <div
                          key={category.slug}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            isEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span>{category.icon}</span>
                            <span className="text-sm font-medium">{category.name}</span>
                          </div>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(checked) => handleToggleCategory(country, category.slug, checked)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Pending Approvals */}
        {pendingJobs.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Pending Approvals ({pendingJobs.length})
              </CardTitle>
              <CardDescription>Jobs waiting for admin approval</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingJobs.map(job => (
                  <div key={job.id} className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div>
                      <h4 className="font-semibold">{job.title}</h4>
                      <p className="text-sm text-gray-600">{job.company_name} • {job.location}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary">{job.category}</Badge>
                        <Badge variant="secondary">{job.country}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => handleApproveJob(job.id, false)}
                      >
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApproveJob(job.id, true)}
                      >
                        <Check className="h-4 w-4 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminJobPostingSettings;
